import React, { PureComponent } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Grid,
  Toolbar,
  withStyles,
  withWidth
} from '@material-ui/core';
import {
  IoChevronForwardOutline,
  IoPulseOutline,
  IoTrendingUpOutline,
  IoWalletOutline
} from 'react-icons/io5';
import PropTypes from 'prop-types';
import { compose } from 'recompose';

import TrendingChart from './TrendingChart';
import { formatCurrency } from '../../helpers/format';

const drawerWidth = 260;

const styles = (theme) => ({
  root: {
    width: drawerWidth,
    flexShrink: 0
  },
  paper: {
    width: drawerWidth,
    backgroundColor: '#181B1C'
  },
  title: {
    flex: 1,
    color: '#9AC802',
    fontSize: theme.spacing(1.5),
    lineHeight: theme.spacing(2.25) + 'px'
  },
  label: {
    color: '#666E79',
    fontSize: theme.spacing(1.125),
    lineHeight: theme.spacing(1.5) + 'px'
  },
  value: {
    fontFamily: 'Roboto Mono',
    fontWeight: '500',
    fontSize: theme.spacing(2.25),
    lineHeight: theme.spacing(3) + 'px'
  }
})

const StyledDivider = withStyles({
  root: {
    backgroundColor: '#272A2D'
  }
})(Divider);

const StyledChip = withStyles(theme => ({
  root: {
    height: theme.spacing(3),
    borderRadius: theme.spacing(0.5)
  },
  colorPrimary: {
    backgroundColor: '#9AC802',
    color: '#181B1C',
    '&:hover': {
      backgroundColor: '#9AC802'
    },
    '&:focus': {
      backgroundColor: '#9AC802'
    }
  },
  label: {
    paddingLeft: theme.spacing(0.75),
    paddingRight: theme.spacing(0.75)
  }
}))(Chip);

const StyledButton = withStyles(theme => ({
  root: {
    width: theme.spacing(8)
  },
  contained: {
    backgroundColor: '#9AC802',
    color: '#111112',
    '&:hover': {
      backgroundColor: '#9AC802'
    },
    '&:focus': {
      backgroundColor: '#9AC802'
    }
  },
  outlined: {
    borderColor: '#9AC802',
    color: '#9AC802'
  },
  label: {
    fontSize: theme.spacing(1.125),
    lineHeight: theme.spacing(1.25) + 'px',
    fontWeight: '500',
    whiteSpace: 'nowrap'
  }
}))(Button);

class Sidebar extends PureComponent {
  render() {
    return this.props.width === 'xs' || this.props.width === 'sm' ? (
      <Drawer
        className={this.props.classes.root}
        variant="temporary"
        classes={{
          paper: this.props.classes.paper
        }}
        anchor="left"
        open={this.props.open}
        onClose={this.props.onClose}
      >
        <Box mx={2}>
          {this.renderOpenTrades()}
          <StyledDivider />
          {this.renderTradingHistory()}
          <StyledDivider />
          {this.renderTrainingWallet()}
        </Box>
      </Drawer>
    ) : (
      <Drawer
        className={this.props.classes.root}
        variant="permanent"
        classes={{
          paper: this.props.classes.paper
        }}
      >
        <Toolbar />
        <Box mx={2}>
          {this.renderOpenTrades()}
          <StyledDivider />
          {this.renderTradingHistory()}
          <StyledDivider />
          {this.renderTrainingWallet()}
        </Box>
      </Drawer>
    );
  }

  renderOpenTrades() {
    return (
      <Box width="100%" mt={4} mb={3}>
        <Box display="flex" alignItems="center" mb={2.5}>
          <IoPulseOutline color="#9AC802" />
          <Box mx={2} className={this.props.classes.title}>OPEN TRADES</Box>
          <IoChevronForwardOutline color="#9AC802" />
        </Box>
        <Grid container>
          <Grid item xs={6}>
            <Box className={this.props.classes.label}>Trades in progress</Box>
          </Grid>
          <Grid item xs={6}>
            <Box className={this.props.classes.label}>Closest outcome</Box>
          </Grid>
          <Grid item xs={6}>
            <Box mt={0.5} className={this.props.classes.value}>15</Box>
          </Grid>
          <Grid item xs={6}>
            <Box
              mt={0.5}
              fontWeight="500"
              fontSize={this.props.theme.spacing(2.5)}
              lineHeight={this.props.theme.spacing(3) + 'px'}
            >
              25m 40s
            </Box>
          </Grid>
          <Grid item xs={6} />
          <Grid item xs={6}>
            <Box
              mt={0.5}
              fontSize={this.props.theme.spacing(1.5)}
              lineHeight={this.props.theme.spacing(2) + 'px'}
            >
              ETH 48 hour
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }

  handleDurationChange = (duration) => () => {
    if (this.props.onDurationChanged) {
      this.props.onDurationChanged(duration);
    }
  }

  renderTradingHistory() {
    return (
      <Box width="100%" mt={4} mb={3}>
        <Box display="flex" alignItems="center" mb={2.5}>
          <IoTrendingUpOutline color="#9AC802" />
          <Box mx={2} className={this.props.classes.title}>TRADING HISTORY</Box>
          <IoChevronForwardOutline color="#9AC802" />
        </Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <StyledChip
            label="Day"
            variant={this.props.duration === 'day' ? 'default' : 'outlined'}
            size="medium"
            color={this.props.duration === 'day' ? 'primary' : 'default'}
            onClick={this.handleDurationChange('day')}
          />
          <StyledChip
            label="Week"
            variant={this.props.duration === 'week' ? 'default' : 'outlined'}
            size="medium"
            color={this.props.duration === 'week' ? 'primary' : 'default'}
            onClick={this.handleDurationChange('week')}
          />
          <StyledChip
            label="Month"
            variant={this.props.duration === 'month' ? 'default' : 'outlined'}
            size="medium"
            color={this.props.duration === 'month' ? 'primary' : 'default'}
            onClick={this.handleDurationChange('month')}
          />
          <StyledChip
            label="All"
            variant={this.props.duration === 'all' ? 'default' : 'outlined'}
            size="medium"
            color={this.props.duration === 'all' ? 'primary' : 'default'}
            onClick={this.handleDurationChange('all')}
          />
        </Box>
        <Grid container>
          <Grid item xs={6}>
            <Box className={this.props.classes.label}>Total trades</Box>
          </Grid>
          <Grid item xs={6}>
            <Box className={this.props.classes.label}>Profit</Box>
          </Grid>
          <Grid item xs={6}>
            <Box mt={0.5} className={this.props.classes.value}>245</Box>
          </Grid>
          <Grid item xs={6}>
            <Box mt={0.5} className={this.props.classes.value}>+21.5%</Box>
          </Grid>
        </Grid>
        <Box mt={2.5}>
          <TrendingChart
            height={this.props.theme.spacing(10)}
            duration={this.props.duration}
          />
        </Box>
      </Box>
    );
  }

  renderTrainingWallet() {
    return (
      <Box width="100%" mt={4} mb={5}>
        <Box display="flex" alignItems="center">
          <IoWalletOutline color="#9AC802" />
          <Box mx={2} className={this.props.classes.title}>TRAINING WALLET</Box>
          <IoChevronForwardOutline color="#9AC802" />
        </Box>
        <Box mt={2.5} className={this.props.classes.label}>Trading</Box>
        <Box display="flex" alignItems="center">
          <Box flex={1} mt={0.5} className={this.props.classes.value}>{formatCurrency(34752)}</Box>
          <StyledButton variant="outlined">TOP UP</StyledButton>
        </Box>
        <Box mt={2} className={this.props.classes.label}>Holding</Box>
        <Box display="flex" alignItems="center">
          <Box
            flex={1}
            mt={0.5}
            fontFamily="Roboto Mono"
            fontSize={this.props.theme.spacing(1.75)}
            lineHeight={this.props.theme.spacing(2.5) + 'px'}
          >
            {formatCurrency(4102)}
          </Box>
          <StyledButton variant="contained">BUY DAI</StyledButton>
        </Box>
      </Box>
    );
  }
}

Sidebar.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  duration: PropTypes.string.isRequired,
  onDurationChanged: PropTypes.func
};

export default compose(
  withStyles(styles, { withTheme: true }),
  withWidth()
)(Sidebar);
